import React, { type ChangeEvent, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import type { SelectOptionGroup } from '../../../config/types';
import { validateField } from '../../../utils/form-utils';
import ErrorMessage from '../ErrorMessage';
import { DefaultCheckbox } from './DefaultCheckbox';
import type { CheckboxGroupProps, CheckboxSubGroupProps } from './Props';

const ConditionalCheckboxSubGroup = ({
  id,
  anchorId,
  currentValue,
  option,
  name,
  disabled,
  handleOnChange,
}: CheckboxSubGroupProps) => (
  <div className="govuk-radios fieldset-container" data-module="govuk-radios" key={option.label}>
    <legend className="govuk-fieldset__legend govuk-fieldset__legend--s">
      <h3 className="govuk-fieldset__heading">{option.label}</h3>
    </legend>
    {option.options.map((option, index) => (
      <DefaultCheckbox
        id={id}
        name={name}
        disabled={disabled}
        key={option}
        anchorId={anchorId}
        index={index}
        item={option as string}
        currentValue={currentValue}
        handleOnChange={handleOnChange}
      />
    ))}
  </div>
);

export default function ConditionalCheckboxField({
  anchorId,
  question,
  disabled,
  id,
}: CheckboxGroupProps) {
  const { options, name, title, rules, errorMessages } = question;
  const resolvedOptions = Array.isArray(options) ? options : [];
  const { register, getFieldState, getValues, setValue, watch } = useFormContext();
  const defaultValue = Array.isArray(question.defaultValue)
    ? question.defaultValue
    : question.defaultValue
      ? [question.defaultValue]
      : [];
  const watchedValue = watch(name, defaultValue);
  const currentValue = Array.isArray(watchedValue) ? watchedValue : defaultValue;

  const handleOnChange = (e: ChangeEvent<HTMLInputElement>, option: string) => {
    const newValues = e.target.checked
      ? [...currentValue, option]
      : currentValue.filter((v: string) => v !== option);

    setValue(name, newValues, { shouldDirty: true, shouldValidate: true });
  };

  useEffect(() => {
    if (getValues(name) === undefined) {
      setValue(name, defaultValue);
    }
  }, [defaultValue, getValues, name, setValue]);

  const hooksFormRegister = register(name, {
    validate: (value: string) => validateField(title, rules || {}, value, errorMessages),
  });
  const { error } = getFieldState(name);

  const optionIsSubGroup: boolean =
    (resolvedOptions[0] && typeof resolvedOptions[0] === 'object' && 'label' in resolvedOptions[0]) ||
    false;

  return (
    <>
      {error && <ErrorMessage error={error} />}
      <div className="govuk-checkboxes" data-module="govuk-checkboxes">
        {resolvedOptions.map((option, i) => (
          <React.Fragment key={i}>
            {optionIsSubGroup && (
              <ConditionalCheckboxSubGroup
                {...hooksFormRegister}
                id={id}
                name={name}
                disabled={disabled}
                anchorId={anchorId}
                option={option as SelectOptionGroup}
                currentValue={currentValue}
                handleOnChange={handleOnChange}
              />
            )}
            {!optionIsSubGroup && (
              <DefaultCheckbox
                {...hooksFormRegister}
                id={id}
                name={name}
                disabled={disabled}
                anchorId={anchorId}
                index={i}
                item={option as string}
                currentValue={currentValue}
                handleOnChange={handleOnChange}
              />
            )}
          </React.Fragment>
        ))}
      </div>
    </>
  );
}
